import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { RecrystaMark } from "@/components/RecrystaLogo";
import { PasswordStrength, checkPasswordPolicy } from "@/components/PasswordStrength";

export default function SignupPage() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    const { pass } = checkPasswordPolicy(password, email);
    if (!pass) {
      setErr("Please choose a password that meets all the requirements below.");
      return;
    }
    setBusy(true);
    const res = await register({ email, password, name, company_name: company });
    setBusy(false);
    if (res.ok) nav("/dashboard");
    else setErr(res.error);
  };

  return (
    <div className="flex min-h-screen w-full">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-24 py-12 bg-white">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2 mb-10">
            <RecrystaMark size={36} />
            <div className="text-lg font-bold tracking-tight text-slate-900">Recrysta</div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900">
            Create your workspace
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Each signup is an isolated tenant. Your data stays yours.
          </p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="signup-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-1">Your name</label>
                <input
                  data-testid="signup-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="block w-full rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  placeholder="Alex Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-900 mb-1">Company</label>
                <input
                  data-testid="signup-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="block w-full rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                  placeholder="Acme Inc."
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1">Email</label>
              <input
                data-testid="signup-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1">Password</label>
              <input
                data-testid="signup-password"
                type="password"
                required
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full rounded-md border-0 py-2 px-3 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm"
                placeholder="At least 10 characters"
              />
              {password && <PasswordStrength password={password} email={email} />}
            </div>
            {err && (
              <div className="text-sm text-red-600" data-testid="signup-error">{err}</div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="signup-submit"
              className="w-full inline-flex items-center justify-center rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition-colors disabled:opacity-60"
            >
              {busy ? "Creating…" : "Create workspace"}
            </button>
          </form>
          <div className="mt-6 text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" data-testid="link-login" className="font-semibold text-blue-600 hover:text-blue-700">
              Sign in
            </Link>
          </div>
        </div>
      </div>
      <div className="hidden lg:flex w-1/2 bg-slate-950 p-12 flex-col justify-between text-white">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500">
            What you get
          </div>
          <ol className="mt-8 space-y-6 text-sm">
            {[
              ["01", "Secure HubSpot OAuth", "Read-only scopes. Tokens encrypted at rest with AES-GCM."],
              ["02", "Daily deal snapshots", "Append-only history — never overwritten, so you can track change over time."],
              ["03", "Stage mapping", "Normalize your pipeline into standard stages for meaningful analysis."],
              ["04", "Data health score", "Know what percentage of your open pipeline value is actually reliable."],
            ].map(([n, k, v]) => (
              <li key={n} className="flex gap-4">
                <div className="text-slate-600 font-mono">{n}</div>
                <div>
                  <div className="font-semibold">{k}</div>
                  <div className="text-slate-400 mt-1">{v}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="text-xs text-slate-600">EU-hosted · GDPR-friendly · Multi-tenant by design</div>
      </div>
    </div>
  );
}
